/* Shared by both frontends — they report into the same Sentry project, so the noise they have to
   drop is the same. Both reach it through a `shared` alias, of their bundler and of tsconfig
   respectively, which is why this stays plain JavaScript: webpack 4 has no loader for TypeScript. */

import {flatMap, startsWith} from './legacy';

/* Safari reports extension code with a source URL of "webkit-masked-url://hidden/". `denyUrls`
   cannot match it: the stack parser knows no such protocol and splits the URL across the frame's
   filename and function, so both have to be checked. */
const MaskedURLPattern = /webkit-masked-url/i;

const isMaskedFrame = frame => (
    MaskedURLPattern.test(frame.filename || '')
    || MaskedURLPattern.test(frame.function || '')
);

const getValues = event => (event.exception && event.exception.values) || [];

const getFrames = event => flatMap(
    getValues(event),
    value => (value.stacktrace && value.stacktrace.frames) || [],
);

/* Third-party noise that runs from our own URL, so `denyUrls` cannot catch it. */
export const IgnoreErrors = [
    /* Benign rendering notification, not an actual error. */
    /ResizeObserver loop/,
    /* WebExtension APIs (browser./runtime. calls) injected by extensions. */
    /Invalid call to (browser|runtime)\./,
];

/* Third-party code we can recognise by where it is loaded from. */
export const DenyURLs = [
    /* Safari extensions carry their own bundle path, e.g. PayPal Honey. */
    /\.safariextension\//i,
];

export const isThirdPartyEvent = event => getFrames(event).some(isMaskedFrame);

export const matchesError = (event, pattern) => getValues(event).some(value => pattern.test(value.value || ''));

/* A page saved to disk keeps loading our scripts from our domain and reports their errors, but
   misses everything the template inlines. Nothing we could fix from here. */
export const isLocalCopy = event => startsWith((event.request && event.request.url) || '', 'file:');

/* Running out of stack is reported by Chrome on iOS and by the Google app, never by Mobile Safari
   on the same engine — those two inject scripts of their own, which would explain it. But our own
   rendering could just as well be what runs out of room there, so these events carry what the page
   held at the time. `__gCrWeb` is the bridge those two put on the window. */
const StackOverflowPattern = /Maximum call stack size exceeded/i;

const isStackOverflow = event => getValues(event).some(value => StackOverflowPattern.test(value.value || ''));

const describePage = () => ({
    elements: document.querySelectorAll('*').length,
    cells: document.querySelectorAll('td').length,
    body: document.body.className,
    /* eslint-disable-next-line no-underscore-dangle */
    injected: Boolean(window.__gCrWeb),
});

export const describeStackOverflow = event => {
    if (typeof document !== 'undefined' && isStackOverflow(event)) {
        event.extra = {...event.extra, page: describePage()};
    }

    return event;
};
